import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { LatestNewsWidget } from "../components/LatestNewsWidget";
import { PendingOrdersWidget } from "../components/PendingOrdersWidget";
import { OtkupPendingWidget } from "../components/OtkupPendingWidget";
import { QualityQueueWidget } from "../components/QualityQueueWidget";
import { SupplierSnapshot } from "../components/SupplierSnapshot";
import { ContractsActionWidget } from "../components/ContractsActionWidget";
import { CooperationSnapshot } from "../components/CooperationSnapshot";
import SalesDashboard from "./SalesDashboard";
import ProcurementDashboard from "./ProcurementDashboard";
import QualityDashboard from "./QualityDashboard";
import type { TFunction } from "i18next";

type QuickLink = { to: string; label: string; description: string; permission?: string };

// Links a customer (CUST_*) sees on the dashboard. Day-to-day work first.
// Contract is intentionally NOT a shortcut — partners sign one rarely (5+ years),
// so the sidebar entry is enough; the dashboard surfaces ongoing collaboration.
const customerLinks = (t: TFunction): QuickLink[] => [
  { to: "/orders/new", label: t("dashboard.link.createOrder.label", "Create order"), description: t("dashboard.link.createOrder.desc", "Start a new order for review by your sales rep."), permission: "orders:create" },
  { to: "/orders", label: t("dashboard.link.myOrders.label", "My orders"), description: t("dashboard.link.myOrders.desc", "Track submitted, in-review and approved orders."), permission: "orders:view" },
  { to: "/forecasts", label: t("dashboard.link.forecasts.label", "Forecasts"), description: t("dashboard.link.forecasts.desc", "Submit demand forecast for upcoming periods."), permission: "forecasts:view" },
  { to: "/catalog", label: t("dashboard.link.catalog.label", "Browse catalog"), description: t("dashboard.link.catalog.desc", "Find products by category, attribute or search."), permission: "catalog:view" },
  { to: "/materials", label: t("dashboard.link.materials.label", "Materials"), description: t("dashboard.link.materials.desc", "Brochures, datasheets, certificates."), permission: "section:materials:read" },
];

// Links an internal, non-sales user (ADMIN/MARKETING/OTKUP/KVALITET/SUPERADMIN) sees.
const internalLinks = (t: TFunction): QuickLink[] => [
  { to: "/catalog", label: t("dashboard.link.catalog.label", "Browse catalog"), description: t("dashboard.link.catalog.desc", "Find products by category, attribute or search."), permission: "catalog:view" },
  { to: "/materials", label: t("dashboard.link.materials.label", "Materials"), description: t("dashboard.link.materials.desc", "Brochures, datasheets, certificates."), permission: "section:materials:read" },
  { to: "/news", label: t("dashboard.link.news.label", "News"), description: t("dashboard.link.news.desc", "Latest Bankom announcements and updates."), permission: "section:news:read" },
  { to: "/admin/companies", label: t("dashboard.link.manageCompanies.label", "Manage companies"), description: t("dashboard.link.manageCompanies.desc", "Customer accounts and sales assignments."), permission: "companies:create" },
  { to: "/admin/users", label: t("dashboard.link.manageUsers.label", "Manage users"), description: t("dashboard.link.manageUsers.desc", "Invite and manage portal users."), permission: "users:invite_customer_any_company" },
  { to: "/admin/catalog/products", label: t("dashboard.link.manageProducts.label", "Manage products"), description: t("dashboard.link.manageProducts.desc", "Catalog content and assets."), permission: "catalog:edit" },
  { to: "/admin/materials", label: t("dashboard.link.uploadMaterials.label", "Upload materials"), description: t("dashboard.link.uploadMaterials.desc", "Add brochures or other DAM assets."), permission: "materials:upload" },
  { to: "/admin/news", label: t("dashboard.link.manageNews.label", "Manage news"), description: t("dashboard.link.manageNews.desc", "Draft, publish or archive articles."), permission: "news:create" },
];

export default function DashboardPage() {
  const { state, hasPermission } = useAuth();
  const { t } = useTranslation();
  if (state.status !== "authenticated") return null;
  const { user } = state;

  // SALES (without admin) gets a focused dashboard centered on companies in
  // their scope and orders awaiting review.
  const isAdmin = user.roles.includes("ADMIN") || user.roles.includes("SUPERADMIN");
  const isSales =
    (user.roles.includes("SALES") || user.roles.includes("SALES_DIRECTOR")) && !isAdmin;
  if (isSales && (hasPermission("section:orders:read") || hasPermission("section:customer_finance:read"))) return <SalesDashboard />;

  // OTKUP without the sales side gets the same control panel read from the other end of the
  // business. The order of the two branches is the decision: a commercial agent carries SALES and
  // OTKUP on one account (rule 2), and for them the selling day is the one that needs steering, so
  // the sales panel wins. The commercial director holds OTKUP alone and lands here.
  const isOtkup = user.roles.includes("OTKUP") && !isAdmin && !isSales;
  if (isOtkup && (hasPermission("section:delivery_intake:read") || hasPermission("section:supplier_finance:read"))) return <ProcurementDashboard />;

  // The laboratory reads the same panel a third way. It comes last of the three because a buyer who
  // also reads the archive still steers the buying day, while the head of quality holds no
  // commercial role at all and lands here on their own.
  const isQuality =
    (user.roles.includes("KVALITET") || user.roles.includes("KVALITET_LEAD"))
    && !isAdmin && !isSales && !isOtkup;
  if (isQuality && (hasPermission("section:lab_archive:read") || hasPermission("section:quality_readings:read"))) return <QualityDashboard />;

  const isCustomer = user.roles.some((r) => r.startsWith("CUST_"));
  const linkSet = isCustomer ? customerLinks(t) : internalLinks(t);
  const visibleLinks = linkSet.filter((l) => {
    // Customer with Forecasts disabled doesn't see the Forecasts shortcut.
    if (l.to === "/forecasts" && isCustomer && !user.forecastsEnabled) return false;
    // Suspended company: ordering is blocked, so no create shortcut.
    if (l.to === "/orders/new" && user.companySuspended) return false;
    return !l.permission || hasPermission(l.permission);
  });

  const hour = new Date().getHours();
  const timeGreetingKey =
    hour < 12 ? "dashboard.morning" : hour < 18 ? "dashboard.afternoon" : "dashboard.evening";
  const timeGreetingFallback =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("dashboard.breadcrumb.aria", "Breadcrumb")}>
            <span>{t("nav.home", { defaultValue: "Home" })}</span>
            <span>{t("nav.dashboard", { defaultValue: "Dashboard" })}</span>
          </nav>
          <h1>
            {t(timeGreetingKey, { defaultValue: timeGreetingFallback })}, {user.firstName}
          </h1>
          {user.companyName && <div className="sub">{user.companyName}</div>}
        </div>
      </div>

      {hasPermission("orders:view") && <CooperationSnapshot />}

      <div className="dashboard-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <h2 style={sectionTitle}>{t("dashboard.quickLinks", "Quick links")}</h2>
          <div style={linkGrid}>
            {visibleLinks.map((l) => (
              <Link key={l.to} to={l.to} style={linkCard}>
                <strong style={{ color: "var(--brand-ink)", fontSize: 14 }}>{l.label}</strong>
                <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{l.description}</span>
              </Link>
            ))}
          </div>
        </div>
        <aside style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {hasPermission("orders:view") && <PendingOrdersWidget limit={5} />}
          {/* Chosen by permission, never by role: the configurable permission
              system is coming, and a dashboard keyed to roles would have to be
              rewritten the day it lands. */}
          {hasPermission("deliveries:confirm") && <OtkupPendingWidget limit={5} />}
          {hasPermission("deliveries:quality") && <QualityQueueWidget limit={6} />}
          {hasPermission("supplier_finance:view_own") && <SupplierSnapshot limit={4} />}
          {/* Contracts widget only for internal users — customers sign one
              every several years, so it's noise on a daily-use dashboard. */}
          {!isCustomer && hasPermission("contracts:view") && <ContractsActionWidget limit={5} />}
          {hasPermission("section:news:read") && <LatestNewsWidget limit={3} />}
        </aside>
      </div>
    </div>
  );
}

const sectionTitle: React.CSSProperties = { margin: 0, fontSize: 13, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const linkGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 };
const linkCard: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4,
  padding: 14, background: "var(--surface)", borderRadius: 10,
  boxShadow: "var(--shadow-1)", textDecoration: "none",
};
